/**
 * BandTourMap — leaflet map of a touring band's stops.
 * Each marker has a popup showing date / venue. Auto-zooms to fit all
 * stops. Uses a small static lookup of common tour cities so we don't
 * need a geocoding API for the typical Sound→PNW→East-Coast circuit.
 */
import { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import { MapSpikingOverlay } from "./SpikingSignsBadge";

// Static geocoding lookup — keys are case-insensitive "city" or "city,state"/"city,country".
// Add to this as touring acts visit new places. Coordinates are city-center.
const CITY_COORDS = {
  // Pacific Northwest
  "tacoma,wa": [47.2529, -122.4443],
  "seattle,wa": [47.6062, -122.3321],
  "olympia,wa": [47.0379, -122.9007],
  "bellingham,wa": [48.7519, -122.4787],
  "renton,wa": [47.4829, -122.2171],
  "sumner,wa": [47.2032, -122.2401],
  "chelan,wa": [47.8390, -120.0151],
  "puyallup,wa": [47.1854, -122.2929],
  "portland,or": [45.5152, -122.6784],
  "vancouver,canada": [49.2827, -123.1207],
  "vancouver,bc": [49.2827, -123.1207],
  // East Coast / DC corridor
  "washington,d.c.": [38.9072, -77.0369],
  "washington,dc": [38.9072, -77.0369],
  "philadelphia,pa": [39.9526, -75.1652],
  "new york city,ny": [40.7128, -74.0060],
  "new york,ny": [40.7128, -74.0060],
  "providence,ri": [41.8240, -71.4128],
  "boston,ma": [42.3601, -71.0589],
  // International
  "tokyo,japan": [35.6762, 139.6503],
  "london,uk": [51.5074, -0.1278],
  "berlin,germany": [52.5200, 13.4050],
};

const lookupCoords = (stop) => {
  const tryKeys = [
    `${stop.city || ""},${stop.state || ""}`.toLowerCase(),
    `${stop.city || ""},${stop.country || ""}`.toLowerCase(),
    `${stop.city || ""}`.toLowerCase(),
  ];
  for (const k of tryKeys) {
    if (CITY_COORDS[k]) return CITY_COORDS[k];
  }
  return null;
};

// Custom pin icon — purple/orange theme, matches THE WALL's accent palette.
// Next-show pin uses a slightly larger size + higher z-index so it stays
// visible even when clustered with other pins.
// When the stop has a flyer_url attached, we render the flyer as the pin
// itself (teardrop with a mini poster) so the map instantly telegraphs
// what each show looks like.
const buildIcon = (isNext, flyerUrl) => {
  const size = isNext ? 42 : 34;
  if (flyerUrl) {
    return L.divIcon({
      className: "",
      html: `<div style="
        width:${size}px;height:${size + 6}px;position:relative;
        filter:drop-shadow(0 3px 6px rgba(0,0,0,.45));
      ">
        <div style="
          width:${size}px;height:${size}px;
          border-radius:50% 50% 50% 0;transform:rotate(-45deg);
          background:${isNext ? "#FFB347" : "#9B59B6"};
          border:3px solid white;
          display:flex;align-items:center;justify-content:center;overflow:hidden;
          ${isNext ? "outline:3px solid #FFB347aa;outline-offset:2px;" : ""}
        ">
          <img src="${flyerUrl}"
               style="width:${size - 6}px;height:${size - 6}px;object-fit:cover;transform:rotate(45deg);border-radius:4px;" />
        </div>
      </div>`,
      iconSize: [size, size + 6],
      iconAnchor: [size / 2, size + 4],
    });
  }
  return L.divIcon({
    className: "",
    html: `<div style="
      width:${isNext ? 34 : 26}px;height:${isNext ? 34 : 26}px;border-radius:50%;
      background:${isNext ? "#FFB347" : "#9B59B6"};
      border:3px solid white;box-shadow:0 2px 10px rgba(0,0,0,.45);
      display:flex;align-items:center;justify-content:center;
      color:white;font-weight:700;font-size:${isNext ? 16 : 12}px;font-family:Georgia,serif;
      ${isNext ? "outline:3px solid #FFB347aa;outline-offset:2px;" : ""}
    ">${isNext ? "★" : "♪"}</div>`,
    iconSize: [isNext ? 34 : 26, isNext ? 34 : 26],
    iconAnchor: [isNext ? 17 : 13, isNext ? 17 : 13],
  });
};

const fmtDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
};

const BandTourMap = ({ stops = [], bandName }) => {
  // Decorate each stop with coords. Prefer the lat/lng the backend already
  // resolved (via businesses lookup + city-center fallback) — the static
  // CITY_COORDS table below is the LAST fallback for stops the backend
  // couldn't resolve. Earlier this was reversed (only static table consulted)
  // which silently dropped every backend-geocoded pin and made Walker's
  // 31-stop tour render as 3 pins.
  const pinned = useMemo(() => {
    return (stops || [])
      .map((s, idx) => {
        const apiCoords = (typeof s.lat === "number" && typeof s.lng === "number")
          ? [s.lat, s.lng]
          : null;
        const coords = apiCoords || lookupCoords(s);
        return { ...s, coords, _idx: idx };
      })
      .filter(s => Array.isArray(s.coords));
  }, [stops]);

  if (pinned.length === 0) return null;

  // Compute bounds — leaflet expects [[lat,lng], …]
  const bounds = pinned.map(s => s.coords);
  // Pad bounds slightly so pins don't sit on the edge
  const center = bounds[0];

  // Polyline showing tour route (chronological)
  const route = pinned.map(s => s.coords);

  return (
    <div className="rounded-2xl overflow-hidden border border-[#E2E0DC] shadow-sm" data-testid="band-tour-map">
      <div className="px-4 py-3 bg-white border-b border-[#E2E0DC] flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold uppercase tracking-widest text-[#9B59B6]">Tour Map</span>
        <span className="text-xs text-[#5C5C5C]">— {pinned.length} cities mapped{pinned.length < stops.length ? ` (${stops.length - pinned.length} pending)` : ""}</span>
        <span className="ml-auto text-[10px] text-[#5C5C5C] flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#FFB347] inline-block" /> Next show</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#9B59B6] inline-block" /> Other stops</span>
        </span>
      </div>
      <div style={{ height: 380 }} className="relative">
        <MapSpikingOverlay testId="bandtour-spiking-badge" />
        <MapContainer
          bounds={bounds.length > 1 ? bounds : undefined}
          center={bounds.length === 1 ? center : undefined}
          zoom={bounds.length === 1 ? 6 : undefined}
          boundsOptions={{ padding: [40, 40] }}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {/* Route polyline — dashed, subtle */}
          {route.length > 1 && (
            <Polyline
              positions={route}
              pathOptions={{ color: "#9B59B6", weight: 2.5, opacity: 0.55, dashArray: "6 8" }}
            />
          )}
          {pinned.map((s, i) => {
            const flyerSrc = s.flyer_url
              ? (s.flyer_url.startsWith("http") ? s.flyer_url : `${window.location.origin}${s.flyer_url}`)
              : null;
            return (
              <Marker
                key={`${s.date}-${s.city}-${i}`}
                position={s.coords}
                icon={buildIcon(i === 0, flyerSrc)}
                zIndexOffset={i === 0 ? 1000 : 0}
              >
                <Popup>
                  <div className="text-sm" style={{ minWidth: 200 }}>
                    {flyerSrc && (
                      <img
                        src={flyerSrc}
                        alt={`${s.city} flyer`}
                        style={{ width: "100%", maxHeight: 180, objectFit: "contain", borderRadius: 6, marginBottom: 8, background: "#111" }}
                      />
                    )}
                    <p className="font-bold mb-0.5" style={{ color: "#9B59B6" }}>{fmtDay(s.date)}</p>
                    <p className="font-semibold m-0">{s.city}{s.state ? `, ${s.state}` : ""}{s.country ? `, ${s.country}` : ""}</p>
                    {s.venue && <p className="text-xs m-0 mt-1" style={{ color: "#5C5C5C" }}>{s.venue}</p>}
                    {(s.time || s.note) && (
                      <p className="text-xs m-0 mt-1" style={{ color: "#5C5C5C" }}>
                        {s.time}{s.time && s.note ? " · " : ""}{s.note}
                      </p>
                    )}
                    {bandName && <p className="text-[10px] m-0 mt-2 italic" style={{ color: "#9B59B6" }}>{bandName}</p>}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
};

export default BandTourMap;
